import React, { FC, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import * as S from './AdForm.styles';
import { Button } from '@/components/UI/Button/Button';
import { Input } from '@/components/UI/Input/Input';
import { AdImages } from '@/components/AdImages/AdImages';
import { ROUTES } from '@/constants/routes';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import {
  AdFormFields,
  CreateAdError,
  CreateAdFormProps,
  PhotoFieldState,
  PhotoFieldType,
} from '@/components/AdForm/AdForm.types';
import { PropertyOption, PropertyType } from '@/api/types/categories.types';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { CategorySelect } from '@/components/AdForm/components/CategorySelect';
import { useCategoryProperties } from '@/hooks/useCategoryProperties';
import { CollectionFormSelect } from '@/components/AdForm/components/CollectionFormSelect';
import { RegionFormSelect } from '@/components/AdForm/components/RegionFormSelect';
import { GetRegionsResponse } from '@/api/types/regions.types';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { RequiredStar } from '@/components/UI/RequiredStar/RequiredStar';
import { useAuthStore } from '@/store/useAuthStore';
import { ContactTypeFormSelect } from '@/components/AdForm/components/ContactTypeFormSelect';
import { AuthIframe } from '@/components/AuthIframe/AuthIframe';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { CREATE_AD } from '@/api/mutations/ads.mutations';
import { CreateAdResponse } from '@/api/types/ads.types';
import {
  buildCreateAdAttributes,
  checkOptionsAutogeneratedTitle,
  getPhotoFieldType,
  mapApiErrorsToFormFields,
  scrollToFirstError,
  shouldResetPhotoField,
} from '@/components/AdForm/AdForm.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { AdStatus } from '@/types';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { QUERY } from '@/constants/common';
import { useNewAdContext } from '@/contexts/NewAdContext';
import useScreenSize from '@/hooks/useScreenSize';
import { formatPhoneNumber } from '@/utils/helpers';
import {
  filterVisibleProperties,
  getHiddenPropertyIds,
} from '@/utils/propertyVisibility';
import { NewAdPageTestIds } from '@/__e2e__/testIds/new-ad-page';
import {
  EmailStatusConfirmedIcon,
  EmailStatusWarningIcon,
} from '@/components/UI/Icons/Icons';
import { useAdSubmissionFlowStore } from '@/store/useAdSubmissionFlowStore';
import { useAuth } from '@/hooks/useAuth';
import { ErrorCodesEnum, ErrorExtension } from '@/api/types/common.types';

export const CreateAdForm: FC<CreateAdFormProps> = ({ onSubmitError }) => {
  const { t } = useTranslation();
  const { isLarge } = useScreenSize();
  const customPush = useCustomPush();
  const [isAuthenticated, userInfo] = useAuthStore(state => [
    state.isAuthenticated,
    state.userInfo,
  ]);
  const methods = useFormContext<AdFormFields>();
  const [isAuthIframeOpen, toggleAuthIFrame] = useToggleOpenState(false);
  const { selectedSubcategory, defaultSubcategory, rootCategory } =
    useNewAdContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setIsAdSubmitRedirect = useAdSubmissionFlowStore(
    state => state.setIsAdSubmitRedirect
  );
  const { categoryProperties } = useCategoryProperties(
    selectedSubcategory?.path,
    !!defaultSubcategory
  );
  const [photoFieldState, setPhotoFieldState] = useState<PhotoFieldState>({
    isVisible: false,
    presetPhotos: null,
  });
  const source = usePlatformSource();
  const [isEmailMsgVisible, setIsEmailMsgVisible] = useState(false);
  const [isPhoneMsgVisible, setIsPhoneMsgVisible] = useState(false);
  const [shouldLoginUserInIframe, setShouldLoginUserInIframe] = useState(true);
  const { loginUser, getBaseUser } = useAuth();
  const subcategoryIdRef = useRef(selectedSubcategory?.id);

  const {
    name: userName,
    email,
    emailConfirmed,
    phone,
    authenticationPhone,
  } = userInfo ?? {};
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setError,
    setValue,
    resetField,
    formState: { isReady },
  } = methods;
  const hasCollectionProperties =
    categoryProperties &&
    categoryProperties.some(p => p.type === PropertyType.CollectionProperty);
  const { isPriceRequired } = selectedSubcategory ?? rootCategory ?? {};
  const { collection = {}, contact, photoIds } = watch();
  const visibleProperties = categoryProperties
    ? filterVisibleProperties(categoryProperties, collection)
    : null;
  const isImagesLoading = photoIds?.some(({ id }) => id.endsWith('-loading'));
  const isTitleFieldVisible =
    selectedSubcategory &&
    !(
      checkOptionsAutogeneratedTitle(collection) &&
      selectedSubcategory.hasAutogeneratedTitle
    );

  const { data: regions } = useQuery<GetRegionsResponse>(GET_REGIONS);

  const [createAd] = useMutation<CreateAdResponse>(CREATE_AD);

  function resetPhotoField() {
    resetField('photoIds');
    setPhotoFieldState({
      isVisible: false,
      presetPhotos: null,
      sourcePropertyLegacyId: null,
    });
  }

  function onSelectPropertyOption(option: PropertyOption) {
    if (getPhotoFieldType(option) !== PhotoFieldType.Hidden) {
      resetField('photoIds');
      setPhotoFieldState({
        isVisible: true,
        presetPhotos: option.photos,
        sourcePropertyLegacyId: option.propertyLegacyId ?? null,
      });
      return;
    }

    const sourceId = photoFieldState.sourcePropertyLegacyId;

    if (!sourceId) return;

    const hiddenPropertyIds = getHiddenPropertyIds(categoryProperties ?? [], {
      ...collection,
      [option.propertyLegacyId]: option,
    });

    if (
      shouldResetPhotoField({
        option,
        sourcePropertyLegacyId: sourceId,
        hiddenPropertyIds,
      })
    ) {
      resetPhotoField();
    }
  }

  function submitAd(data: AdFormFields) {
    setIsSubmitting(true);
    const visibleIds = visibleProperties
      ? new Set(visibleProperties.map(p => p.legacyResourceId))
      : undefined;
    const adParams = buildCreateAdAttributes(data, source, visibleIds);
    createAd({ variables: { adParams } }).then(response => {
      if (!response.data?.createAd) return;

      const { entity, errors = [] } = response.data.createAd;
      const hasAuthError = errors.find(({ path }) => path?.includes('user'));

      const isUnconfirmed = (extensions: ErrorExtension | undefined) =>
        extensions?.code === ErrorCodesEnum.UnconfirmedPhoneError;

      const hasUnconfirmedPhoneError = errors.find(({ extensions }) =>
        isUnconfirmed(extensions)
      );
      let formErrors = mapApiErrorsToFormFields<CreateAdError>(errors);

      if (
        hasUnconfirmedPhoneError &&
        (!isAuthenticated || formErrors.length === 1)
      ) {
        formErrors = formErrors.filter(
          ({ extensions }) => !isUnconfirmed(extensions)
        );
      }

      if (formErrors.length) {
        onSubmitError(formErrors);

        formErrors.forEach(({ name, message }) => {
          if (name === 'photoIds' && !photoFieldState.isVisible) return;
          if (name === 'title' && !isTitleFieldVisible) return;
          setError(name, { message });
        });

        scrollToFirstError(formErrors);
        setIsSubmitting(false);
        return;
      }

      if (hasAuthError || hasUnconfirmedPhoneError) {
        setShouldLoginUserInIframe(!hasUnconfirmedPhoneError || !!hasAuthError);
        toggleAuthIFrame(true);
        return;
      }

      if (entity && entity.path && entity.status) {
        setIsAdSubmitRedirect(true);
        customPush(
          entity.status === AdStatus.AwaitingPayment
            ? `${entity.path}#${QUERY.awaitingPayment}`
            : entity.path
        );
      } else {
        setIsSubmitting(false);
      }
    });
  }

  function onSubmit(data: AdFormFields) {
    submitAd(data);
  }

  function showEmailMsg() {
    setIsEmailMsgVisible(true);
  }

  function showPhoneMsg() {
    if (!isAuthenticated) {
      setIsPhoneMsgVisible(true);
    }
  }

  useEffect(() => {
    if (isSubmitting) return;
    if (typeof userName === 'string') setValue('contact.name', userName);
    if (typeof email === 'string') setValue('contact.email', email);
    const defaultPhone = formatPhoneNumber(phone || authenticationPhone);
    if (defaultPhone) setValue('contact.phones', [defaultPhone]);
  }, [userInfo]);

  useEffect(() => {
    if (isReady) {
      setValue('categoryId', selectedSubcategory?.id || '', {
        shouldDirty: true,
      });
    }
    if (subcategoryIdRef.current === selectedSubcategory?.id) return;
    subcategoryIdRef.current = selectedSubcategory?.id;
    resetField('photoIds');
  }, [isReady, selectedSubcategory?.id, setValue, resetField]);

  useEffect(() => {
    setPhotoFieldState({
      isVisible:
        !!selectedSubcategory &&
        !!categoryProperties &&
        !hasCollectionProperties,
      presetPhotos: null,
      sourcePropertyLegacyId: null,
    });
  }, [selectedSubcategory, categoryProperties, hasCollectionProperties]);

  const renderEmailField = (
    field: ControllerRenderProps<AdFormFields, 'contact.email'>,
    fieldState: ControllerFieldState
  ) => {
    const isEmailUnchanged = field.value === email;
    const hasEmailStatusIcon = emailConfirmed != null && isEmailUnchanged;
    const shouldShowEmailWarning =
      (emailConfirmed === false && isEmailUnchanged) || isEmailMsgVisible;

    return (
      <S.EmailFieldWrapper>
        <S.EmailInputWrapper>
          <Input
            {...field}
            id="lot_contact_attributes_email"
            onFocus={showEmailMsg}
            appearance="filled"
            label={t('pages.new_ad.email')}
            required
            withEllipsis
            type="email"
            helperText={fieldState.error?.message}
            hasError={!!fieldState.error}
            disabled={!!emailConfirmed}
            hasAdditionalPadding={hasEmailStatusIcon}
          />
          {hasEmailStatusIcon && (
            <S.EmailStatusIcon>
              {emailConfirmed ? (
                <EmailStatusConfirmedIcon />
              ) : (
                <EmailStatusWarningIcon />
              )}
            </S.EmailStatusIcon>
          )}
        </S.EmailInputWrapper>
        {shouldShowEmailWarning && (
          <S.EmailWarningDescription>
            {t('pages.new_ad.email_warning_description')}
          </S.EmailWarningDescription>
        )}
      </S.EmailFieldWrapper>
    );
  };

  const renderSubmitBox = () => (
    <S.AdFormSubmitBox>
      <S.AdFormMsg $variant="2">
        <Trans
          i18nKey="pages.new_ad.agree_gdpr_policy"
          components={{
            link1: <CustomLink target="_blank" href={ROUTES.terms} />,
          }}
        />
      </S.AdFormMsg>
      <Button
        appearance="primary"
        isFullWidth
        disabled={isSubmitting || isImagesLoading}
        type="submit"
        data-stat={`${isLarge ? '' : 'mob-'}new-lot-submit-${
          isAuthenticated ? '' : 'un'
        }authorized`}
        borderRadius={14}
      >
        {t('pages.new_ad.create_ad')}
      </Button>
    </S.AdFormSubmitBox>
  );

  return (
    <S.AdForm
      data-testid={NewAdPageTestIds.form}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      onKeyDown={e => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
          e.preventDefault();
        }
      }}
    >
      <S.AdFormSectionCard>
        <CategorySelect />

        {selectedSubcategory &&
          visibleProperties &&
          visibleProperties.map(p => {
            switch (p.type) {
              case PropertyType.CollectionProperty:
                return (
                  <CollectionFormSelect
                    key={p.legacyResourceId}
                    propertyLegacyId={p.legacyResourceId}
                    options={p.options}
                    popularOptions={p.popularOptions}
                    collection={collection}
                    allProperties={visibleProperties}
                    onSelectPropertyOption={onSelectPropertyOption}
                  />
                );
              case PropertyType.RangeProperty:
                return (
                  <Controller
                    key={p.legacyResourceId}
                    name={`range.${p.legacyResourceId}`}
                    control={control}
                    shouldUnregister
                    render={({
                      field: { name, value, onChange },
                      fieldState,
                    }) => (
                      <Input
                        name={name}
                        value={value as string}
                        onChange={onChange}
                        appearance="filled"
                        precision={Infinity}
                        inputMode="decimal"
                        label={p.name}
                        required={p.isRequired}
                        helperText={fieldState.error?.message}
                        hasError={!!fieldState.error}
                      />
                    )}
                  />
                );
              case PropertyType.BooleanProperty:
                return (
                  <Controller
                    key={p.legacyResourceId}
                    name={`boolean.${p.legacyResourceId}`}
                    control={control}
                    shouldUnregister
                    render={({ field, fieldState }) => (
                      <Checkbox
                        {...field}
                        dataTestId={`checkbox-boolean-${p.legacyResourceId}`}
                        key={p.legacyResourceId}
                        appearance="filledCheckIcon"
                        label={p.name}
                        helperText={fieldState.error?.message}
                        hasError={!!fieldState.error}
                      />
                    )}
                  />
                );
              case PropertyType.StringProperty:
                return (
                  <Controller
                    key={p.legacyResourceId}
                    name={`collection.${p.legacyResourceId}`}
                    control={control}
                    shouldUnregister
                    render={({
                      field: { name, value, onChange },
                      fieldState,
                    }) => (
                      <Tooltip
                        content={p.hint}
                        disabled={!isLarge || !p.hint}
                        allowedTriggers={['focus']}
                        fallbackPlacements={['top-end']}
                        offset={10}
                      >
                        <Input
                          name={name}
                          value={value as string}
                          onChange={onChange}
                          appearance="filled"
                          label={p.name}
                          required={p.isRequired}
                          helperText={fieldState.error?.message}
                          hasError={!!fieldState.error}
                        />
                      </Tooltip>
                    )}
                  />
                );
              case PropertyType.PhoneNumberProperty:
                return (
                  <Controller
                    key={p.legacyResourceId}
                    name={`collection.${p.legacyResourceId}`}
                    control={control}
                    shouldUnregister
                    render={({
                      field: { name, value, onChange },
                      fieldState,
                    }) => (
                      <Tooltip
                        content={p.hint}
                        disabled={!isLarge || !p.hint}
                        allowedTriggers={['focus']}
                        fallbackPlacements={['top-end']}
                        offset={10}
                      >
                        <Input
                          name={name}
                          value={value as string}
                          onChange={onChange}
                          type="tel"
                          appearance="filled"
                          placeholder="(000) 000-00-00"
                          label={p.name}
                          required={p.isRequired}
                          inputMode="numeric"
                          hasBorderBottom={false}
                          helperText={fieldState.error?.message}
                          hasError={!!fieldState.error}
                        />
                      </Tooltip>
                    )}
                  />
                );
              default:
                return null;
            }
          })}
      </S.AdFormSectionCard>

      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>
          {t('pages.new_ad.city_and_price')}
        </S.AdFormSectionTitle>
        <RegionFormSelect regions={regions?.regions} />
        <Controller
          name="price"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              id="lot_price"
              appearance="filled"
              precision={2}
              inputMode="decimal"
              label={t('pages.new_ad.price')}
              required={isPriceRequired}
              helperText={fieldState.error?.message}
              hasError={!!fieldState.error}
            />
          )}
        />
      </S.AdFormSectionCard>

      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>
          {t(
            isTitleFieldVisible
              ? 'pages.new_ad.title_and_description'
              : 'common.description'
          )}
        </S.AdFormSectionTitle>
        {isTitleFieldVisible && (
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <Tooltip
                content={t('pages.new_ad.title_hint')}
                allowedTriggers={['focus']}
                fallbackPlacements={['top-end']}
                disabled={!isLarge}
                offset={10}
              >
                <Input
                  {...field}
                  appearance="filled"
                  label={t('common.title')}
                  required
                  maxLength={100}
                  helperText={fieldState.error?.message}
                  hasError={!!fieldState.error}
                />
              </Tooltip>
            )}
          />
        )}
        <Controller
          name="body"
          control={control}
          render={({ field, fieldState }) => (
            <Tooltip
              allowedTriggers={['focus']}
              fallbackPlacements={['top-end']}
              disabled={!isLarge}
              offset={10}
              content={t('pages.new_ad.description_hint')}
            >
              <Textarea
                {...field}
                id="lot_body"
                appearance="quartenary"
                height={isLarge ? 245 : 140}
                placeholder={t('pages.new_ad.mark_benefits')}
                withSymbolsCount
                symbolsCountTextPlacement="bottom"
                helperText={fieldState.error?.message}
                helperTextAlignment="right"
                hasError={!!fieldState.error}
              />
            </Tooltip>
          )}
        />
      </S.AdFormSectionCard>

      {photoFieldState.isVisible && (
        <S.AdFormSectionCard>
          <S.AdFormSectionTitle>
            {t('common.photo')}{' '}
            {selectedSubcategory?.isPhotoRequiredOnCreate && <RequiredStar />}
          </S.AdFormSectionTitle>
          <Controller
            name="photoIds"
            control={control}
            render={({ field: { value, ...rest }, fieldState }) => (
              <AdImages
                {...rest}
                selected={value}
                presetImages={photoFieldState.presetPhotos}
                helperText={fieldState.error?.message}
                hasError={!!fieldState.error}
              />
            )}
          />
        </S.AdFormSectionCard>
      )}

      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>
          {t('pages.new_ad.contact_details')}
        </S.AdFormSectionTitle>
        <Controller
          name="contact.name"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              id="lot_contact_attributes_name"
              appearance="filled"
              label={t('pages.new_ad.name')}
              withEllipsis
              helperText={fieldState.error?.message}
              hasError={!!fieldState.error}
            />
          )}
        />
        <Controller
          name="contact.email"
          control={control}
          render={({ field, fieldState }) =>
            renderEmailField(field, fieldState)
          }
        />
        {isAuthenticated && (
          <ContactTypeFormSelect name="contact.contactType" control={control} />
        )}
        <Controller
          name="contact.phones.0"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              id="lot_contact_attributes_phones_separated"
              type="tel"
              appearance="filled"
              placeholder="(000) 000-00-00"
              onFocus={showPhoneMsg}
              label={t('pages.new_ad.mobile_phone')}
              required
              inputMode="numeric"
              hasBorderBottom={false}
              helperText={fieldState.error?.message}
              hasError={!!fieldState.error}
            />
          )}
        />
        {isPhoneMsgVisible && (
          <S.AdFormPhoneMsgBox>
            <S.AdFormPhoneMsg $variant="3">
              {t('pages.new_ad.mobile_phone_tip_1')}
            </S.AdFormPhoneMsg>
            <S.AdFormPhoneMsgInBox $variant="3">
              {t('pages.new_ad.mobile_phone_tip_2')}
            </S.AdFormPhoneMsgInBox>
          </S.AdFormPhoneMsgBox>
        )}
        {!isLarge && renderSubmitBox()}
      </S.AdFormSectionCard>

      {isLarge && renderSubmitBox()}

      <AuthIframe
        isOpen={isAuthIframeOpen}
        phones={[contact?.phones?.[0] || '']}
        close={() => toggleAuthIFrame(false)}
        onSuccess={async accessToken => {
          const values = getValues();
          if (shouldLoginUserInIframe) {
            await loginUser({ variables: { accessToken } });
            await getBaseUser();
            submitAd(values);
          } else {
            await getBaseUser();
            submitAd({
              ...values,
              contact: { ...values.contact, jwt: accessToken },
            });
          }
        }}
        onManualClose={() => setIsSubmitting(false)}
      />
    </S.AdForm>
  );
};

CreateAdForm.displayName = 'CreateAdForm';
